import { Plugin } from 'vite';
import * as path from 'path';
import fs from 'fs-extra';
import * as glob from 'glob';
import { createTypeScriptPlugin, extractRouteParams, Route } from './TypeScriptPlugin.js';
import { ConfigLoader } from '../config/ConfigLoader.js';

// Helper to only write files when content changes to avoid unnecessary HMR triggers
async function writeFileIfChanged(filePath: string, content: string): Promise<boolean> {
	try {
		const exists = await fs.pathExists(filePath);
		if (exists) {
			const existingContent = await fs.readFile(filePath, 'utf-8');
			if (existingContent === content) {
				return false; // No change needed
			}
		}
		await fs.writeFile(filePath, content, 'utf-8');
		return true; // File was written
	} catch (error) {
		// If there's an error reading, just write the file
		await fs.writeFile(filePath, content, 'utf-8');
		return true;
	}
}

export function createRouterPlugin(viewsPath: string): Plugin {
	let lastKnownFiles: string[] = [];

	return {
		name: 'frame-router',
		async configureServer(server) {
			// Watch for changes in views directory
			server.watcher.add(viewsPath);

			// Watch for configuration changes
			const configFiles = ['frame.config.js', 'frame.config.mjs', 'frame.config.ts'];
			const cwd = path.dirname(path.dirname(viewsPath));

			configFiles.forEach(configFile => {
				const configPath = path.join(cwd, configFile);
				if (fs.existsSync(configPath)) {
					server.watcher.add(configPath);
				}
			});

			// Generate routes when server starts and store file list
			await generateRoutes(viewsPath);
			lastKnownFiles = await getCurrentViewFiles(viewsPath);

			// Listen for file changes in views directory
			server.watcher.on('change', async (file) => {
				// Handle config file changes
				if (configFiles.some(cf => file.endsWith(cf))) {
					console.log('[Frame Router] Configuration changed, regenerating routes...');
					await generateRoutes(viewsPath);
					return;
				}

				// Handle view file changes
				if (file.includes('/views/') && file.endsWith('.vue')) {
					// Check if the file list has changed (files added/removed)
					const currentFiles = await getCurrentViewFiles(viewsPath);
					const filesChanged = hasFileListChanged(lastKnownFiles, currentFiles);

					if (filesChanged) {
						console.log('[Frame Router] Views files added/removed, regenerating routes...');
						await generateRoutes(viewsPath);
						lastKnownFiles = currentFiles;
					} else {
						console.log('[Frame Router] View content changed, letting HMR handle it...');
					}
				}
			});

			// Listen for file additions/deletions
			server.watcher.on('add', async (file) => {
				if (file.includes('/views/') && file.endsWith('.vue')) {
					console.log('[Frame Router] New view file added, regenerating routes...');
					await generateRoutes(viewsPath);
					lastKnownFiles = await getCurrentViewFiles(viewsPath);
				}
			});

			server.watcher.on('unlink', async (file) => {
				if (file.includes('/views/') && file.endsWith('.vue')) {
					console.log('[Frame Router] View file removed, regenerating routes...');
					await generateRoutes(viewsPath);
					lastKnownFiles = await getCurrentViewFiles(viewsPath);
				}
			});
		},
		async buildStart() {
			// Generate routes at build start
			await generateRoutes(viewsPath);
		},
	};
}

async function getCurrentViewFiles(viewsPath: string): Promise<string[]> {
	try {
		const vueFiles = glob.sync('**/*.vue', {
			cwd: viewsPath,
			absolute: true
		});
		return vueFiles.sort();
	} catch (error) {
		console.warn('[Frame Router] Error getting view files:', error);
		return [];
	}
}

function hasFileListChanged(oldFiles: string[], newFiles: string[]): boolean {
	if (oldFiles.length !== newFiles.length) {
		return true;
	}

	const oldSorted = [...oldFiles].sort();
	const newSorted = [...newFiles].sort();

	return oldSorted.some((file, index) => file !== newSorted[index]);
}

async function generateRoutes(viewsPath: string): Promise<void> {
	console.log('[Frame Router] Generating routes from:', viewsPath);

	// Get project root directory
	const cwd = path.dirname(path.dirname(viewsPath));

	// Load frame configuration for custom routes
	const configLoader = new ConfigLoader(cwd);
	const frameConfig = await configLoader.load();

	// Find all Vue files in the views directory
	const vueFiles = glob.sync('**/*.vue', {
		cwd: viewsPath,
		absolute: true
	});

	console.log('[Frame Router] Found files:', vueFiles);

	// Generate route objects from files with parameter extraction
	const fileBasedRoutes: Route[] = vueFiles.map(filePath => {
		const relativePath = path.relative(viewsPath, filePath);
		const routeName = path.basename(relativePath, '.vue');

		// Convert file path to route path
		let routePath = '/' + relativePath
			.replace(/\.vue$/, '') // Remove .vue extension
			.replace(/\/index$/, '') // Remove /index for index files
			.replace(/\\/g, '/'); // Normalize path separators

		// Handle special cases
		if (routePath === '/Home' || routePath === '/home') {
			routePath = '/';
		}

		// Convert PascalCase to kebab-case for consistency
		if (routePath !== '/') {
			// Split the path and convert each segment separately
			const pathSegments = routePath.split('/');
			routePath = pathSegments.map(segment => {
				if (segment === '' || segment.startsWith(':') || segment.startsWith('[')) {
					// Don't modify empty segments, route parameters, or dynamic segments
					return segment;
				}
				// Convert PascalCase to kebab-case: MetaDemo -> meta-demo
				return segment.replace(/([A-Z])/g, (match, letter, index) => {
					return index === 0 ? letter.toLowerCase() : '-' + letter.toLowerCase();
				});
			}).join('/');

		}

		// Convert to lowercase for consistency, but preserve dynamic segments
		// e.g., [id].vue -> /:id, [slug].vue -> /:slug
		routePath = routePath.replace(/\[([^\]]+)\]/g, ':$1');

		// Extract parameters for type generation
		const params = extractRouteParams(routePath);

		return {
			path: routePath,
			name: routeName,
			component: filePath,
			params
		};
	});

	// Process custom routes from configuration
	const customRoutes: any[] = [];
	if (frameConfig.routing?.customRoutes) {
		for (const customRoute of frameConfig.routing.customRoutes) {
			customRoutes.push({
				path: customRoute.path,
				name: customRoute.name,
				redirect: customRoute.redirect,
				// Resolve a relative component path against the project root — it is emitted as a
				// literal `import('<component>')` in src/.frame/routes.ts, so a './src/...' path
				// (relative to .frame/) would not resolve. Absolute paths are passed through.
				component: customRoute.component && !path.isAbsolute(customRoute.component)
					? path.resolve(cwd, customRoute.component)
					: customRoute.component,
				meta: customRoute.meta,
				props: customRoute.props,
				beforeEnter: customRoute.beforeEnter
			});
		}
		console.log(`[Frame Router] Added ${customRoutes.length} custom routes`);
	}

	// Filter out file-based routes overridden by a custom route — by PATH or by NAME.
	// (Two routes sharing a `name` make vue-router render an empty view, so a custom
	// catch-all reusing a file-based view's name must displace it.)
	const customRoutePaths = new Set(customRoutes.map(r => r.path));
	const customRouteNames = new Set(customRoutes.map(r => r.name).filter(Boolean));
	const filteredFileBasedRoutes = fileBasedRoutes.filter(route => {
		if (customRoutePaths.has(route.path)) {
			console.log(`[Frame Router] Custom route overrides file-based route: ${route.path}`);
			return false;
		}
		if (customRouteNames.has(route.name)) {
			console.log(`[Frame Router] Custom route overrides file-based route by name: ${route.name}`);
			return false;
		}
		return true;
	});

	// Combine all routes (file-based routes only, custom routes handled separately)
	const allRoutes = [...filteredFileBasedRoutes];

	// Generate the routes file
	const frameDir = path.join(cwd, 'src', '.frame');
	await fs.ensureDir(frameDir);

	// Generate TypeScript types first (only for file-based routes with components)
	const tsPlugin = createTypeScriptPlugin(cwd, allRoutes);
	await tsPlugin.buildStart();

	// Generate the routes.ts file
	const routesContent = generateRoutesContent(allRoutes, customRoutes);
	const routesPath = path.join(frameDir, 'routes.ts');
	const wasWritten = await writeFileIfChanged(routesPath, routesContent);

	if (wasWritten) {
		console.log('[Frame Router] Routes generated successfully at:', routesPath);
	}
}

function generateRoutesContent(fileBasedRoutes: Route[], customRoutes: any[]): string {
	const imports = fileBasedRoutes.map((route, index) =>
		`import View${index} from '${route.component}';`
	).join('\n');

	const fileBasedRouteObjects = fileBasedRoutes.map((route, index) => {
		return `  {
    path: '${route.path}',
    name: '${route.name}',
    component: View${index}${route.params && route.params.length > 0 ? `,
    props: true // Enable route params as props` : ''}
  }`;
	}).join(',\n');

	// Generate custom route objects
	const customRouteObjects = customRoutes.map(route => {
		let routeObj = `  {
    path: '${route.path}',
    name: '${route.name}'`;

		if (route.redirect) {
			if (typeof route.redirect === 'function') {
				routeObj += `,
    redirect: ${route.redirect.toString()}`;
			} else {
				routeObj += `,
    redirect: '${route.redirect}'`;
			}
		}

		if (route.component) {
			routeObj += `,
    component: () => import('${route.component}')`;
		}

		if (route.meta) {
			routeObj += `,
    meta: ${JSON.stringify(route.meta)}`;
		}

		if (route.props !== undefined) {
			routeObj += `,
    props: ${typeof route.props === 'boolean' ? route.props : JSON.stringify(route.props)}`;
		}

		if (route.beforeEnter) {
			routeObj += `,
    beforeEnter: ${route.beforeEnter.toString()}`;
		}

		routeObj += `
  }`;

		return routeObj;
	}).join(',\n');

	// Combine all route objects
	const customParts = customRouteObjects.trim() ? customRouteObjects.split(',\n').filter(r => r.trim()) : [];
	const fileParts = fileBasedRouteObjects.trim() ? fileBasedRouteObjects.split(',\n').filter(r => r.trim()) : [];
	const allRouteObjects = [...customParts, ...fileParts].join(',\n');

	return `// Auto-generated routes - do not edit manually
${imports}
import { createRouter, createWebHistory } from 'vue-router';

export const routes = [
${allRouteObjects}
];

// Export route information for type checking
export const routeInfo = {
${fileBasedRoutes.map(route => `  '${route.name}': { path: '${route.path}', params: [${route.params?.map(p => `'${p}'`).join(', ') || ''}] }`).join(',\n')}
};

// Create and export the router instance
const router = createRouter({
  history: createWebHistory(),
  routes,
  // Restore on back/forward, jump to #anchors, otherwise scroll to top on navigation.
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) return { el: to.hash, behavior: 'smooth' };
    return { top: 0 };
  }
});

export default router;
`;
} 